import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { FormikProps } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { FreelancerOnboardingMyProfessionalInformationStepViewModel } from "../../../../../ViewModels/FreelancerOnboardingMyProfessionalInformationStepViewModel";

interface MissionPreferencesSectionProps {
	formik: FormikProps<FreelancerOnboardingMyProfessionalInformationStepViewModel>;
	missionsDurations: readonly { id: number; label: string }[];
	jobLocations: readonly { id: number; label: string }[] | undefined;
	missionDurationRef?: React.RefObject<HTMLInputElement>;
	locationPreferenceRef?: React.RefObject<HTMLInputElement>;
}

const MissionPreferencesSection: React.FC<MissionPreferencesSectionProps> = ({
	formik,
	missionsDurations,
	jobLocations,
	missionDurationRef,
	locationPreferenceRef
}) => {
	const { t } = useTranslation();

	return (
		<Box sx={{ mb: 4 }}>
			<Typography variant="h4" sx={{ mb: 2 }}>
				{t(
					"freelancerOnboardingMyProfessionalInformation.missionDurationAndLocation.title"
				)}
			</Typography>
			<Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
				{t(
					"freelancerOnboardingMyProfessionalInformation.missionDurationAndLocation.description"
				)}
			</Typography>
			<Box sx={{ display: "flex", gap: 2, mt: 4 }}>
				{/* Mission Duration */}
				<Box sx={{ flex: 1 }}>
					<Autocomplete
						value={formik.values.preferredMissionDuration}
						onChange={(_, newValue) =>
							formik.setFieldValue("preferredMissionDuration", newValue)
						}
						options={missionsDurations}
						getOptionLabel={option => t(`MissionsDurations.${option.label}`)}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						renderInput={params => (
							<TextField
								{...params}
								inputRef={missionDurationRef}
								name={"preferredMissionDuration"}
								onBlur={formik.handleBlur}
								placeholder={t(
									"freelancerOnboardingMyProfessionalInformation.missionDuration.placeholder"
								)}
								variant="outlined"
								label={t(
									"freelancerOnboardingMyProfessionalInformation.missionDuration.title"
								)}
								error={
									formik.touched.preferredMissionDuration &&
									Boolean(formik.errors.preferredMissionDuration)
								}
								helperText={
									formik.touched.preferredMissionDuration &&
									formik.errors.preferredMissionDuration
								}
							/>
						)}
					/>
				</Box>

				{/* Location Preference */}
				<Box sx={{ flex: 1 }}>
					<Autocomplete
						value={formik.values.preferredLocation}
						onChange={(_, newValue) =>
							formik.setFieldValue("preferredLocation", newValue)
						}
						options={jobLocations ?? []}
						getOptionLabel={option => t(`JobLocations.${option.label}`)}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						renderInput={params => (
							<TextField
								{...params}
								inputRef={locationPreferenceRef}
								name={"preferredLocation"}
								onBlur={formik.handleBlur}
								placeholder={t(
									"freelancerOnboardingMyProfessionalInformation.locationPreference.placeholder"
								)}
								variant="outlined"
								label={t(
									"freelancerOnboardingMyProfessionalInformation.locationPreference.title"
								)}
								error={
									formik.touched.preferredLocation &&
									Boolean(formik.errors.preferredLocation)
								}
								helperText={
									formik.touched.preferredLocation && formik.errors.preferredLocation
								}
							/>
						)}
					/>
				</Box>
			</Box>
		</Box>
	);
};

export default MissionPreferencesSection;
